import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { useSpring } from 'react-spring';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useScrollDirection from 'utils/hooks/useScrollDirection';
import { ScheduleAnAppointmentModal } from 'components/components/molecules';
import SubNavigationContactModal from 'components/components/molecules/SubNavigationContactModal';
import { SubNavigationFloatingBar } from 'components/components/organisms';
import { useUiValue, openRegistrationModal } from 'stores/uiContext';
import { getDefaultFontSize } from 'utils/getDefaultFontSize';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import { getSalesCenterMapHref } from 'utils/getMapLink';

const getCTA = (rendering) => {
  try {
    const fields = rendering.placeholders['btn-cta'][0].fields;
    return getFieldValue(fields, 'buttonCta');
  } catch (e) {
    console.log(e);
    return {};
  }
};
const getScheduleAppointmentCTA = (rendering) => {
  try {
    const fields = rendering.placeholders['btn-cta'][0].fields;
    return getFieldValue(fields, 'scheduleAppointmentCTA');
  } catch (e) {
    console.log(e);
    return {};
  }
};
const getScheduleAppointmentModal = (rendering) => {
  try {
    const fields = rendering.placeholders['btn-cta'][0].fields;
    return getFieldValue(fields, 'scheduleAppointment');
  } catch (e) {
    console.log(e);
    return {};
  }
};

const getPortalContentHeight = (id) => {
  if (typeof document === 'undefined') return 0;

  const portal = document.getElementById(id);
  const element = portal && (portal.firstElementChild || portal);

  if (!element) return 0;

  return Math.ceil(
    element.getBoundingClientRect().height || element.clientHeight || 0,
  );
};

const getPortalContentBottom = (id) => {
  if (typeof document === 'undefined') return 0;

  const portal = document.getElementById(id);
  const element = portal && (portal.firstElementChild || portal);

  if (!element) return 0;

  const rect = element.getBoundingClientRect();

  return rect.height > 0 && rect.bottom > 0 ? Math.ceil(rect.bottom) : 0;
};

const getVisualViewportTop = () => {
  if (typeof window === 'undefined') return 0;

  return Math.max(0, window.visualViewport?.offsetTop || 0);
};

const isIOSChrome = () => {
  if (typeof navigator === 'undefined') return false;

  return /CriOS/i.test(navigator.userAgent);
};

const getNotificationTopHeight = () => {
  const stackBottom = Math.max(
    getPortalContentBottom('national-notification-banner'),
    getPortalContentBottom('market-notification-banner'),
    getPortalContentBottom('notification-banner'),
  );
  const measuredHeight =
    getPortalContentHeight('national-notification-banner') +
    getPortalContentHeight('market-notification-banner') +
    getPortalContentHeight('notification-banner');
  const chromeViewportAdjustedHeight =
    measuredHeight + (isIOSChrome() ? getVisualViewportTop() : 0);

  return Math.max(stackBottom, chromeViewportAdjustedHeight);
};

const SubNavigationBar = ({ fields, rendering }) => {
  const title = getFieldValue(fields, 'title');
  const firstAddress = getFieldValue(fields, 'firstAddress');
  const secondAddress = getFieldValue(fields, 'secondAddress');
  const city = getFieldValue(fields, 'city');
  const stateAbbreviation = getFieldValue(fields, 'stateAbbreviation');
  const zipcode = getFieldValue(fields, 'zipcode');
  const cta = getCTA(rendering);
  const scheduleAppointmentCTA = getScheduleAppointmentCTA(rendering);
  const scheduleAppointmentModal = getScheduleAppointmentModal(rendering);

  const [
    {
      notificationActive,
      notificationHeight,
      nationalNotificationHeight,
      hasScheduleAppointmentCTA,
      salesCenterContact,
    },
    dispatch,
  ] = useUiValue();
  const scrollDirection = useScrollDirection();

  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const [isScheduleAppointmentModalOpen, setIsScheduleAppointmentModalOpen] =
    useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isContactBarVisible, setIsContactBarVisible] = useState(false);
  const [defaultFontSize, setDefaultFontSize] = useState();
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);
  const floatingBarVisible =
    fixedBarVisible && !(isMobile && isContactBarVisible);

  useEffect(() => {
    const font = getDefaultFontSize();
    setDefaultFontSize(font);
  }, []);

  const animatedRef = useRef();

  const [notificationTopHeight, setNotificationTopHeight] = useState(0);
  useEffect(() => {
    const fallbackHeight = notificationHeight + nationalNotificationHeight;

    const syncNotificationTopHeight = () => {
      const measuredHeight = getNotificationTopHeight();
      setNotificationTopHeight(measuredHeight || fallbackHeight);
    };

    let rafId = 0;
    let timeoutId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);

      rafId = requestAnimationFrame(syncNotificationTopHeight);
      timeoutId = window.setTimeout(syncNotificationTopHeight, 250);
    };

    scheduleSync();
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync, { passive: true });
    window.addEventListener('orientationchange', scheduleSync);
    window.visualViewport?.addEventListener('resize', scheduleSync);
    window.visualViewport?.addEventListener('scroll', scheduleSync);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('orientationchange', scheduleSync);
      window.visualViewport?.removeEventListener('resize', scheduleSync);
      window.visualViewport?.removeEventListener('scroll', scheduleSync);
    };
  }, [notificationHeight, nationalNotificationHeight]);

  const fixedBarStyle = useSpring({
    config: { duration: isMobile ? 0 : 100 },
    top: floatingBarVisible
      ? scrollDirection === 'up'
        ? defaultFontSize <= 18
          ? notificationTopHeight + 110 //80
          : notificationTopHeight + 150
        : defaultFontSize <= 18
        ? notificationTopHeight // 0
        : notificationTopHeight + 70 // 70
      : -110, // 100
    backgroundColor: '#fff',
  });

  const handleOnScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const offset = notificationActive ? 360 : 300;
    if (currentScrollY > offset) {
      setFixedBarVisible(true);
    } else if (fixedBarVisible) {
      setFixedBarVisible(false);
    }
  }, [fixedBarVisible, notificationActive]);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  useEffect(() => {
    if (!isMobile) {
      setIsContactBarVisible(false);
      return undefined;
    }

    if (
      typeof window === 'undefined' ||
      typeof window.IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const contactBarElement = window.document.querySelector(
      '[data-sub-navigation-contact-bar]',
    );

    if (!contactBarElement) {
      return undefined;
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsContactBarVisible(entry.isIntersecting);
    });

    observer.observe(contactBarElement);

    return () => observer.disconnect();
  }, [isMobile]);

  const streetAddress = [firstAddress, secondAddress].filter(Boolean).join(' ');
  const cityStateZip = [city, stateAbbreviation, zipcode]
    .filter(Boolean)
    .join(' ');
  const address = [streetAddress, cityStateZip].filter(Boolean).join(', ');
  const salesCenterForMap = {
    ...salesCenterContact,
    address1: salesCenterContact.address1 || firstAddress,
    address2: salesCenterContact.address2 || secondAddress,
    city: salesCenterContact.city || city,
    stateAbbreviation:
      salesCenterContact.stateAbbreviation || stateAbbreviation,
    zipcode: salesCenterContact.zipcode || zipcode,
  };
  const mapHref = getSalesCenterMapHref(salesCenterForMap);
  const phoneNumberCall =
    salesCenterContact && salesCenterContact.phoneNumberCall;
  const email = salesCenterContact && salesCenterContact.email;
  const phoneHref = phoneNumberCall && phoneNumberCall.replace(/[^0-9+]/g, '');

  const handleCTAClick = () => {
    //window.dataLayer.push({ event: 'register' });
    trackAnalyticsEvent({
      gtagEvent: 'StickyNavFormClick',
      mouseflowTag: 'StickyNavForm',
    });
    dispatch(openRegistrationModal());
  };

  const handleScheduleAppointmentCTAClick = () => {
    trackAnalyticsEvent({
      gtagEvent: 'StickyNavApptClick',
      mouseflowTag: 'StickyNavAppt',
    });
    setIsScheduleAppointmentModalOpen(true);
  };

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  return (
    <>
      {hasScheduleAppointmentCTA && scheduleAppointmentCTA && (
        <ScheduleAnAppointmentModal
          isOpen={isScheduleAppointmentModalOpen}
          onClose={() => setIsScheduleAppointmentModalOpen(false)}
          fields={scheduleAppointmentModal}
        />
      )}
      {isMobile && (
        <SubNavigationContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          salesCenter={salesCenterContact}
          scheduleAppointment={scheduleAppointmentModal}
          communityTitle={title}
        />
      )}
      <SubNavigationFloatingBar
        title={title}
        address={address}
        mapHref={mapHref}
        phoneNumberCall={phoneNumberCall}
        phoneHref={phoneHref}
        email={email}
        cta={cta}
        scheduleAppointmentCTA={scheduleAppointmentCTA}
        hasScheduleAppointmentCTA={hasScheduleAppointmentCTA}
        fixedBarVisible={floatingBarVisible}
        fixedBarStyle={fixedBarStyle}
        animatedRef={animatedRef}
        useDisplayNoneWhenHidden={false}
        onContactClick={handleContactClick}
        onCTAClick={handleCTAClick}
        onScheduleAppointmentCTAClick={handleScheduleAppointmentCTAClick}
      />
    </>
  );
};

export default SubNavigationBar;
